import React, { useRef } from 'react';
import { useTheme } from 'styled-components';

import Text from 'shared/components/Text/Text';
import { Checkbox } from 'shared/components/inputs/Checkbox/Checkbox';
import {
  DISABLED_DROPDOWN_STATE,
  ENABLED_DROPDOWN_STATE,
  getDropdownStyleVariantValues,
  TDropdownDataItem,
  TDropdownStyleVariantsKey,
} from 'shared/design-system/theme/dropdowns';
import {
  DropdownItemButtonWrapper,
  DropdownItemStyled,
  DropdownTitle,
  IconContainer,
} from './DropdownItemStyles';

export type TDropdownItemProps = {
  id?: string;
  ariaRole?: 'listitem' | 'option';
  dropdownItemData: TDropdownDataItem;
  styleVariant?: TDropdownStyleVariantsKey;
  onClick?: (e: React.MouseEvent<HTMLLIElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLLIElement>) => void;
  onMouseMove?: (e: React.MouseEvent<HTMLLIElement>) => void;
  onMouseEnter?: (e: React.MouseEvent<HTMLLIElement>) => void;
  fontSize?: number;
  // Multi-select props
  $isSelected?: boolean;
  $isMultiSelect?: boolean;
};

/**
 * @param {string} [id] - id for the dropdown item (optional)
 * @param {'listitem' | 'option'} [ariaRole] - ARIA role for accessibility, defaults to 'listitem' (optional)
 * @param {TDropdownDataItem} dropdownItemData - Item data containing title, value, Icon, and disabled state (required)
 * @param {TDropdownStyleVariantsKey} [styleVariant] - Style variant for theming, defaults to 'default' (optional)
 * @param {function} [onClick] - Click handler for item selection (e: React.MouseEvent<HTMLLIElement>) => void (optional)
 * @param {function} [onKeyDown] - Keydown handler for keyboard navigation (e: React.KeyboardEvent<HTMLLIElement>) => void (optional)
 * @param {function} [onMouseMove] - Mouse move handler (e: React.MouseEvent<HTMLLIElement>) => void (optional)
 * @param {function} [onMouseEnter] - Mouse enter handler for hover effects (e: React.MouseEvent<HTMLLIElement>) => void (optional)
 * @param {number} [fontSize] - Custom font size for the item text (optional)
 * @param {boolean} [$isSelected] - Whether the item is currently selected (for multi-select mode) (optional)
 * @param {boolean} [$isMultiSelect] - Whether the dropdown is in multi-select mode, renders checkbox when true (optional)
 */

export function DropdownItem({
  id,
  dropdownItemData,
  onClick,
  onKeyDown,
  onMouseMove,
  onMouseEnter,
  styleVariant = 'default',
  ariaRole = 'listitem',
  fontSize,
  // Multi-select props
  $isSelected = false,
  $isMultiSelect = false,
}: TDropdownItemProps) {
  const theme = useTheme();
  const { title, Icon, isDisabled } = dropdownItemData;
  const buttonRef = useRef<HTMLButtonElement>(null);
  const testId = `${title?.split(' ').join('-').toLowerCase()}-dropdownItem`;

  const buttonState = isDisabled ? DISABLED_DROPDOWN_STATE : ENABLED_DROPDOWN_STATE;
  const { iconColor } = getDropdownStyleVariantValues(theme, styleVariant, buttonState);

  const handleClick = (e: React.MouseEvent<HTMLLIElement>) => {
    e.stopPropagation();
    onClick?.(e);
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    onClick?.(e as any);
  };

  // Multi-select with checkbox
  if ($isMultiSelect) {
    return (
      <DropdownItemStyled
        data-testid={testId}
        id={id}
        role={ariaRole}
        $styleVariant={styleVariant}
        $state={buttonState}
        onClick={handleClick}
        onKeyDown={onKeyDown}
        onMouseMove={onMouseMove}
        onMouseEnter={onMouseEnter}
        $hasIcon={false}
      >
        <DropdownItemButtonWrapper
          ref={buttonRef}
          type="button"
          aria-label={title}
          aria-selected={$isSelected}
          $styleVariant={styleVariant}
          $state={buttonState}
          tabIndex={isDisabled ? -1 : undefined}
          $asCheckboxContainer
        >
          <Checkbox
            id={`${testId}-checkbox`}
            isChecked={$isSelected}
            onChange={handleCheckboxChange}
            text={
              <Text as="h5" $fontSize={fontSize} $margin="0 0 0 24px">
                {title}
              </Text>
            }
            margin="0"
          />
        </DropdownItemButtonWrapper>
      </DropdownItemStyled>
    );
  }

  return (
    <DropdownItemStyled
      data-testid={testId}
      id={id}
      role={ariaRole}
      $styleVariant={styleVariant}
      $state={buttonState}
      onClick={handleClick}
      onKeyDown={onKeyDown}
      onMouseMove={onMouseMove}
      onMouseEnter={onMouseEnter}
      $hasIcon={!!Icon}
    >
      <DropdownItemButtonWrapper
        ref={buttonRef}
        type="button"
        role="button"
        aria-label={title}
        $styleVariant={styleVariant}
        $state={buttonState}
        tabIndex={isDisabled ? -1 : undefined}
      >
        {Icon && (
          <IconContainer>
            <Icon color={iconColor} />
          </IconContainer>
        )}
        <DropdownTitle>
          <Text as="h5" $fontSize={fontSize}>
            {title}
          </Text>
        </DropdownTitle>
      </DropdownItemButtonWrapper>
    </DropdownItemStyled>
  );
}
